<template>
  <tr
    class="group child:border-b child:border-gray-200 hover:bg-blue-50 transition-colors duration-200 cursor-pointer"
    :data-test="`labour-row-${labour.id}`"
    @click="openTable"
  >
    <td class="p-4">
      <div class="flex items-center space-x-3">
        <button
          type="button"
          class="btn-icon btn-icon-default"
          data-test="open-labour"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-transform duration-200"
            :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <div class="flex items-center space-x-2">
          <button
            v-if="projectsStore.project?.unarchived"
            type="button"
            class="btn-icon btn-icon-blue"
            data-test="add-record"
            title="Add Record"
            @click.stop="openRecordCreate()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>
          </button>

          <button
            v-if="projectsStore.project?.unarchived"
            type="button"
            class="btn-icon btn-icon-blue"
            data-test="edit"
            title="Edit Labour"
            @click.stop="$emit('showUpdateModal', labour.id)"
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
          </button>
        </div>

        <div class="flex w-80 flex-col">
          <p class="font-semibold text-gray-900 text-wrap">
            {{ labour.first_name }}
          </p>

          <p
            class="text-sm text-gray-600 text-wrap"
            data-test="labour-last-name"
          >
            {{ labour.last_name }}
          </p>
        </div>
      </div>
    </td>

    <td
      class="p-4 text-gray-700"
      data-test="labour-supplier-name"
    >
      {{ labour.supplier?.name }}
    </td>

    <td
      class="p-4 text-gray-700"
      data-test="labour-cw-ref"
    >
      {{ labour.cw_ref }}
    </td>

    <td class="p-4">
      <span
        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        :class="labour.type === 'internal' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'"
      >
        {{ labour.type }}
      </span>
    </td>

    <td
      class="p-4 text-right font-medium text-gray-900"
      data-test="labour-rate"
    >
      {{ $tcc(labour.rate) }}/{{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.hr') }}
    </td>

    <td
      class="p-4 text-right font-medium text-gray-900"
      data-test="labour-total-hours"
    >
      {{ $tn(labour.totalHours) }}
    </td>

    <td
      class="p-4 text-right font-medium text-gray-900"
      data-test="labour-total-accrual"
    >
      {{ $tcc(labour.totalAccrual) }}
    </td>

    <td
      class="p-4 text-right font-medium text-gray-900"
      data-test="labour-paid-to-date"
    >
      {{ $tcc(labour.paidToDate) }}
    </td>

    <td
      class="p-4 text-right font-semibold text-blue-600"
      data-test="labour-total"
    >
      {{ $tcc(labour.paidToDate + labour.totalAccrual) }}
    </td>
  </tr>

  <!-- Expanded Details Row -->
  <tr
    v-if="open"
    class="bg-gray-50"
  >
    <td
      colspan="100%"
      class="p-4 border-b border-gray-200"
    >
      <Transition
        name="labour-row"
        enter-active-class="animate__animated animate__fadeIn animate__slow"
      >
        <div
          v-if="infiniteScroll.items.value.length"
          v-collapsible="{ duration: 1000, show: open && infiniteScroll.firstLoad.value }"
          v-bind="infiniteScroll.containerProps"
          class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-y-auto max-h-[390px]"
        >
          <table class="w-full border-separate border-spacing-0">
            <thead class="">
              <tr class="child:border-b child:border-gray-200 bg-gray-50 sticky top-0 child:py-3 child:px-2">
                <th class="w-28 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider" />

                <th class="text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.approvedBy') }}
                </th>

                <th class="text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.date') }}
                </th>

                <th class="text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.wbsCode') }}
                </th>

                <th
                  class="text-left text-xs font-semibold text-gray-900 uppercase tracking-wider"
                  title="The area captured on the Mobile app for the day for that specific WBS item"
                >
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.area') }}
                </th>

                <th
                  class="text-left text-xs font-semibold text-gray-900 uppercase tracking-wider"
                  title="The element captured on the Mobile app for the day for that specific WBS item"
                >
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.element') }}
                </th>

                <th
                  class="text-left text-xs font-semibold text-gray-900 uppercase tracking-wider"
                  title="The activity captured on the Mobile app for the day for that specific WBS item"
                >
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.activity') }}
                </th>

                <th class="text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.time') }}
                </th>

                <th class="text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.hours') }}
                </th>

                <th
                  class="text-right text-xs font-semibold text-gray-900 uppercase tracking-wider"
                  title="The cost based on recorded hours that are still to be paid"
                >
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.accrual') }}
                </th>

                <th class="text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.costPaid') }}
                </th>

                <th
                  class="text-center text-xs font-semibold text-gray-900 uppercase tracking-wider"
                  title="The supplier's reference number to either invoice or docket"
                >
                  {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.table.vendorRef') }}
                </th>
              </tr>
            </thead>

            <tbody class="bg-white divide-y divide-gray-100">
              <LabourRowRecord
                v-for="record in infiniteScroll.items.value"
                :key="record.id"
                :record="record"
                :data-test="`labour-record-${record.id}`"
                :labour="labour"
                class="h-[65px]"
                @select-record="selectRecord"
                @show-comments="showRecordComments"
              />
            </tbody>
          </table>
        </div>

        <div
          v-else-if="infiniteScroll.firstLoad.value && !infiniteScroll.loading.value"
          class="bg-white rounded-lg border border-gray-200 p-4 text-center"
        >
          <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
    viewBox="0 0 24 24"
    fill="none"
 class="w-6 h-6 text-gray-400">
    <path
        d="M8 5.00005C7.01165 5.00082 6.49359 5.01338 6.09202 5.21799C5.71569 5.40973 5.40973 5.71569 5.21799 6.09202C5 6.51984 5 7.07989 5 8.2V17.8C5 18.9201 5 19.4802 5.21799 19.908C5.40973 20.2843 5.71569 20.5903 6.09202 20.782C6.51984 21 7.07989 21 8.2 21H15.8C16.9201 21 17.4802 21 17.908 20.782C18.2843 20.5903 18.5903 20.2843 18.782 19.908C19 19.4802 19 18.9201 19 17.8V8.2C19 7.07989 19 6.51984 18.782 6.09202C18.5903 5.71569 18.2843 5.40973 17.908 5.21799C17.5064 5.01338 16.9884 5.00082 16 5.00005M8 5.00005V7H16V5.00005M8 5.00005V4.70711C8 4.25435 8.17986 3.82014 8.5 3.5C8.82014 3.17986 9.25435 3 9.70711 3H14.2929C14.7456 3 15.1799 3.17986 15.5 3.5C15.8201 3.82014 16 4.25435 16 4.70711V5.00005"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
    />
</svg>
          </div>

          <h3 class="font-semibold text-gray-900 mb-2">
            {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.noRecordsFound') }}
          </h3>

          <p class="text-gray-600">
            {{ $t('SelfPerform.LabourDashboard.LabourList.LabourListRow.noRecordsFoundSubtext') }}
          </p>
        </div>

        <div
          v-else-if="infiniteScroll.loading.value"
          class="bg-white rounded-lg border border-gray-200 p-4"
        >
          <AppSpinner class="w-full flex justify-center items-center h-[60px]" />
        </div>
      </Transition>
    </td>
  </tr>

  <!-- Modals -->
  <RecordModal
    v-if="show"
    :record-id="selectedRecord"
    :labour="labour"
    @close="show = false; fetchIfOpen()"
    @split="switchSplit"
  />

  <RecordSplitModal
    v-if="showSplit"
    :project-id="labour.project_id"
    :record-id="selectedRecord"
    @close="showSplit = false; fetchIfOpen()"
  />

  <RecordCommentModal
    v-if="showComments"
    :project-id="labour.project_id"
    :record-id="selectedRecord"
    @close="showComments = false"
  />
</template>

<script setup lang="ts">
import { useInfiniteScroll } from '@/composables/useInfiniteScroll';
import Labour from '@/models/Labour';
import LabourDailyRecord from '@/models/LabourDailyRecord';
import { useLabourDailyRecordStore } from '@/store/labourDailyRecord';
import { useLaboursStore } from '@/store/labours';
import { useProjectsStore } from '@/store/projects';
import { nextTick, ref, watch } from 'vue';
import LabourRowRecord from './LabourRowRecord.vue';
import RecordCommentModal from './RecordCommentModal.vue';
import RecordModal from './RecordModal.vue';
import RecordSplitModal from './RecordSplitModal.vue';

const props = defineProps<{
  labour: Labour;
}>();

defineEmits<{
  (e: 'showUpdateModal', labourId: Id);
}>();

const show = ref(false);
const selectedRecord = ref();

const selectRecord = (id: Id) => {
  selectedRecord.value = id;
  show.value = true;
};

const showSplit = ref(false);

const switchSplit = () => {
  show.value = false;
  showSplit.value = true;
};

const showComments = ref(false);

const showRecordComments = (id: Id) => {
  selectedRecord.value = id;
  showComments.value = true;
};

const laboursStore = useLaboursStore();
const labourDailyRecordsStore = useLabourDailyRecordStore();
const open = ref(false);

const infiniteScroll = useInfiniteScroll<LabourDailyRecord>({
  fetchFunction: async (cursor?: string) => {
    const response = await labourDailyRecordsStore.fetchRecords(props.labour.id, {
      ...laboursStore.filter.form,
      cursor,
    });

    const ids = response.data.map((record) => record.id);

    const modelsById = new Map(
      labourDailyRecordsStore.models.query().whereId(ids).with('wbsCode').with('user').get().map(
        (model) => [model.id, model],
      ),
    );

    const models = ids
      .map((id) => modelsById.get(id))
      .filter((model): model is LabourDailyRecord => !!model);

    return {
      data: models,
      next_cursor: response.next_cursor ?? undefined,
      prev_cursor: response.prev_cursor ?? undefined,
      has_more: response.has_more,
    };
  },

  immediate: false,
  itemHeight: 65,
});

const initialFetch = async () => {
  labourDailyRecordsStore.models.query().where('labour_id', props.labour.id).delete();
  infiniteScroll.reset();
  await infiniteScroll.loadMore();
};

const openRecordCreate = () => {
  selectedRecord.value = undefined;
  show.value = true;

  if(!open.value) {
    open.value = true;
    initialFetch();
  }
};

const fetchIfOpen = () => {
  if(open.value) {
    nextTick(() => {
      initialFetch();
    });
  }
};

const openTable = () => {
  open.value = !open.value;

  if(open.value) {
    initialFetch();
  }
};

watch(() => laboursStore.filter.form, () => {
  if(open.value) {
    initialFetch();
  }
}, { deep: true });

const projectsStore = useProjectsStore();
</script>

<style scoped></style>
